import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import { Link as MuiLink } from '@mui/material';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Stack,
  Grid2,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Iconify } from '../Minimal/src/components/iconify';
import CommonDatePicker from '../datepicker/CommonDatePicker';
import config from "../common/Config";
import * as XLSX from 'xlsx';
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";

const DaySummaryReport = () => {
  const [reportrange, setReportrange] = useState('');
  const [dates, setDates] = useState([null, null]);
  const [reportDate, setReportDate] = useState('');
  const [salesRapOptions, setSalesRapOptions] = useState([]);
  const [salesRap, setSalesRap] = useState('');
  const [routeOptions, setRouteOptions] = useState([]);
  const [salesRoute, setSalesRoute] = useState('');
  const [selectedSalesRep, setSelectedSalesRep] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [daySummaryReport, setDaySummaryReport] = useState([]);
  const [totals, setTotals] = useState({ sum_grand_total: 0, sum_amount: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');

  const handleDateChange = (value) => {
    setDates(value);
    if (value && value[0] && value[1]) {
      const startDate = format(value[0], 'dd/MM/yyyy');
      const endDate = format(value[1], 'dd/MM/yyyy');
      setReportrange(startDate + ' - ' + endDate);
      setReportDate(startDate + ' To Date : ' + endDate);
      setPage(0);
    }
  };

  const fetchSalesRapOptions = async () => {
    const apiSalesRapUrl = `${config.API_URL}/getSalesRep`;
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const body = new FormData();
      if (useEncrypt) {
        const ep = encryptPayload({});
        if (ep) {
          body.append('encrypted_payload', ep);
        }
      }
      const response = await fetch(apiSalesRapUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: body.entries().next().done ? undefined : body
      });
      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const formattedOptions = (data.data || []).map(item => ({
        key: `${item.first_name} ${item.last_name}`,
        value: item.id,
        label: `${item.first_name} ${item.last_name}`,
      }));
      setSalesRapOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchSalesRoute = async () => {
    const apiSalesRouteUrl = `${config.API_URL}/getRoute`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        }
      });
      const data = await response.json();
      const formattedOptions = (data.data || []).map(item => ({
        key: `${item.route_number}`,
        value: item.id,
        label: `${item.route_number}`,
      }));
      setRouteOptions(formattedOptions);
    } catch (error) {
    }
  };

  const fetchReport = useCallback(async () => {
    if (!reportrange) return;

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const formData = new FormData();
      const plain = {
        draw: 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: searchQuery,
        reportrange,
        sales_rep: salesRap || '',
        route_id: salesRoute || ''
      };
      if (useEncrypt) {
        const ep = encryptPayload(plain);
        if (ep) {
          formData.append('encrypted_payload', ep);
        } else {
          Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
        }
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }

      const response = await fetch(`${config.API_URL}/ws-day-summary-report`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to fetch report');

      const raw = await response.json();
      const json = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;

      if (json && json.data && Array.isArray(json.data)) {
        setDaySummaryReport(json.data);
        setTotalCount(json.recordsTotal || json.data.length);
        setTotals({
          sum_grand_total: parseFloat(json.sum_grand_total || 0),
          sum_amount: parseFloat(json.sum_amount || 0)
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [accessToken, token, page, rowsPerPage, reportrange, salesRap, salesRoute, searchQuery]);

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSalesRapOptions();
    fetchSalesRoute();
  }, []);

  useEffect(() => {
    if (reportrange) {
      fetchReport();
    }
  }, [reportrange, page, rowsPerPage, salesRap, salesRoute, searchQuery, fetchReport]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSalesRepChange = (event, newValue) => {
    setSelectedSalesRep(newValue);
    setSalesRap(newValue ? newValue.value : '');
    setPage(0);
  };

  const handleRouteChange = (event, newValue) => {
    setSelectedRoute(newValue);
    setSalesRoute(newValue ? newValue.value : '');
    setPage(0);
  };

  const searchFormData = () => {
    if (dates[0] && dates[1]) {
      const startDate = format(dates[0], 'dd/MM/yyyy');
      const endDate = format(dates[1], 'dd/MM/yyyy');
      setReportrange(startDate + ' - ' + endDate);
      setReportDate(startDate + ' To Date : ' + endDate);
      setPage(0);
    }
  };

  const exportToExcel = async () => {
    const settings = await getCompanySettings();
    const useEncrypt = resolveCustomerApiEncrypt(settings);
    const formData = new FormData();
    const plain = {
      draw: '1',
      start: '0',
      length: totalCount || 10000,
      searchValue: searchQuery,
      reportrange,
      sales_rep: salesRap || '',
      route_id: salesRoute || ''
    };
    if (useEncrypt) {
      const ep = encryptPayload(plain);
      if (ep) {
        formData.append('encrypted_payload', ep);
      } else {
        Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
      }
    } else {
      Object.entries(plain).forEach(([k, v]) => formData.append(k, v ?? ''));
    }

    const apiActivePdtUrl = `${config.API_URL}/ws-day-summary-report`;

    try {
      const response = await fetch(apiActivePdtUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch Products');
      }

      const raw = await response.json();
      const data = normalizeEncryptedApiResponse(raw, useEncrypt) ?? raw;
      const exportData = (data.data || []).map((product, index) => ({
        "Sr.no": index + 1,
        Date: product.date || '',
        "Customer (A/C)": product.company
          ? `${product.company} (${product.accound_no || ''})`
          : product.accound_no || '',
        "Order Number": product.reference_no || '',
        "Grand Total": product.grand_total || '',
        "Sales Rep Collected": product.amount || '',
        "Taken By": product.taken_by || '',
        "Status": product.sale_status || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Day Summary Report');

      const now = new Date();
      const formattedDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const formattedTime = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
      const filename = `DaySummaryReport-${formattedDate}-${formattedTime}.xlsx`;

      XLSX.writeFile(wb, filename);
    } catch (error) {
    }
  };

  const formatNumber = (num) => {
    return parseFloat(num || 0).toFixed(2);
  };

  return (
    <>
      {/* <Header /> */}
      <Box className="page-container">
        <Paper
          className="paper-style"
        >
          <Box sx={{ px: 2, py: 1.2 }}>
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: 1, gap: 2 }}>
              <Typography className="page-title">
                Day Summary Report
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="eva:download-outline" width={20} />}
                onClick={exportToExcel}
                className="button-variant-outlined"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Export to Excel
              </Button>
            </Box>

            <Grid2 container spacing={1}>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  size="small"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline" width={20} sx={{ color: "#0e0f0fef" }} />
                        </InputAdornment>
                      ),
                      endAdornment: searchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSearchQuery('');
                              setPage(0);
                            }}
                            sx={{ color: "#0e0f0fef" }}
                          >
                            <Iconify icon="eva:close-fill" width={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
               
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>

                <CommonDatePicker
                  className="input-border"
                  value={dates}
                  onChange={handleDateChange}
                  format="dd/MM/yyyy"
                  character=" - "
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>

                <Autocomplete
                  options={salesRapOptions}
                  value={selectedSalesRep}
                  onChange={handleSalesRepChange}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      className="input-border text-field"
                      {...params}
                      placeholder="Select Sales Rep"
                      size="small"
                    />
                  )}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 2 }}>

                <Autocomplete
                  options={routeOptions}
                  value={selectedRoute}
                  onChange={handleRouteChange}
                  getOptionLabel={(option) => option.label || ""}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      className="input-border text-field"
                      {...params}
                      placeholder="Select Route"
                      size="small"
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 1 }} display="flex" alignItems="flex-end">
                <Button
                  variant="contained"
                  onClick={searchFormData}
                  className="button-variant-contained"
                  fullWidth
                >
                  Submit
                </Button>
              </Grid2>
            </Grid2>
            </Box>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} />
              </Box>
            ) : (
              <TableContainer>
                <Table size="medium" className="table-cell-border">
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell
                        sx={{

                          width: "80px",
                          minWidth: "80px",

                        }}
                      >
                        Sr.no
                      </TableCell>
                      <TableCell
                        sx={{

                          width: "100px",
                          minWidth: "100px",

                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell
                        sx={{

                          width: "200px",
                          minWidth: "200px",
                        }}
                      >
                        Customer (A/C)
                      </TableCell>
                      <TableCell
                        sx={{

                          width: "150px",
                          minWidth: "150px",

                        }}
                      >
                        Order No
                      </TableCell>
                      <TableCell
                        sx={{

                          width: "120px",
                          minWidth: "120px",
                          textAlign: "right",
                        }}
                      >
                        Inv Amt
                      </TableCell>
                      <TableCell
                        sx={{

                          width: "120px",
                          minWidth: "120px",
                          textAlign: "right",
                        }}
                      >
                        Collected Amt
                      </TableCell>
                      <TableCell
                        sx={{

                          width: "120px",
                          minWidth: "120px",
                      
                        }}
                      >
                        Taken by
                      </TableCell>
                      <TableCell
                        sx={{

                          width: "100px",
                          minWidth: "100px",
                        }}
                      >
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {daySummaryReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" sx={{ color: "#637381" }}>
                            {reportrange ? "No data available" : "Please select a date range and click Submit"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      daySummaryReport.map((row, index) => (
                        <TableRow
                          key={index}
                          className="table-cell-style"
                          sx={{
                            "&:last-child td": {
                              borderBottom: "none",
                            },
                            
                          }}
                        >
                          <TableCell

                          >
                            {index + 1 + page * rowsPerPage}
                          </TableCell>
                          <TableCell

                          >
                            {row.date || ""}
                          </TableCell>
                          <TableCell

                          >
                            {row.company ? `${row.company} (${row.accound_no || ''})` : ""}
                          </TableCell>
                          <TableCell

                          >
                            <MuiLink
                              href={`${config.BASE_URL}/reports/mis_report/print/${row.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ color: "#212b36", textDecoration: "underline", "&:hover": { textDecoration: "underline" } }}
                            >
                              {row.reference_no || ""}
                            </MuiLink>
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "right",
                            }}
                          >
                            {formatNumber(row.grand_total)}
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: "right",
                            }}
                          >
                            {formatNumber(row.amount)}
                          </TableCell>
                          <TableCell

                          >
                            {row.taken_by || ""}
                          </TableCell>
                          <TableCell

                          >
                            {row.sale_status || ""}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow
                      className="table-cell-style"
                      sx={{
                        "& .MuiTableCell-root": {
                          fontWeight: 600,
                   
                          textAlign: "right",
                          backgroundColor: "#f5f7fa",
                        },
                      }}
                    >
                      <TableCell colSpan={3} sx={{ borderBottom: "none" }} />
                      <TableCell

                      >
                        Total:
                      </TableCell>
                      <TableCell

                      >
                        {formatNumber(totals.sum_grand_total)}
                      </TableCell>
                      <TableCell

                      >
                        {formatNumber(totals.sum_amount)}
                      </TableCell>
                      <TableCell colSpan={2} sx={{ borderBottom: "none" }} />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}

            {!loading && daySummaryReport.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", borderTop: "1px solid rgba(145, 158, 171, 0.16)" }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Rows per page:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}
                  className="pagination-styles"
                />
              </Box>
            )}
   
        </Paper>
      </Box>

    </>
  );
};

export default DaySummaryReport;
